import React, { useEffect, useState } from 'react';
import { useIsMutating } from 'react-query';
import { Link, Route, Routes, useMatch, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import * as Page from '../../../components/content/page-header';
import { Header, SubTitle, Tenant, Title } from '../../../components/content/page-header';
import { Sidebar } from '../../../components/layout/sidebar';
import { Content, ContentWrapper } from '../../../components/layout/content';
import { useQuery } from '../../../hooks/use-query';
import { LayerTree, LayerTypes } from '../../geo-info/types/map-layer';
import { General } from './general';
import { Structure } from './structure';
import { Icons } from '../../../components/icons/icons';
import { getColorFromString } from '../../../utils/style';
import { Parents } from './parents';
import { Translations } from '../../geo-info/map-layers/tabs/translations';
import { LightBlack, Primary } from '../../../components/button/button';

type FolderProps = {
  layerId: string;
  offset: number;
};

const Tree = (props: FolderProps): JSX.Element => {
  const navigate = useNavigate();
  const params = useParams();
  const configId = params.configId as string;
  const [searchParams] = useSearchParams();
  const layerId = searchParams.get('selectedLayerId') || props.layerId;
  const layerTree = useQuery<LayerTree>({ path: `/map-layers/tree/${props.layerId}?configurationId=${configId}`, options: { keepPreviousData: true } });
  const [open, setOpen] = useState<boolean>(layerTree.data?.layer?.defaultOpen || false);

  const clickHandler = (): void => {
    if (layerTree.data?.children.length && (!open || (open && searchParams.get('selectedLayerId') === props.layerId))) {
      setOpen(!open);
      return;
    }
    const layerId = params.layerId as string;
    const configId = params.configId as string;
    navigate({ pathname: `/configurations/${configId}/contents/${layerId}`, search: `selectedLayerId=${props.layerId}` });
  };

  useEffect(() => {
    if (layerTree.data?.layer.defaultOpen) {
      setOpen(true);
    }
  }, [layerTree.data?.layer]);

  const isFolder = layerTree?.data?.layer?.type === LayerTypes.FOLDER;
  const isVirtualLayer = layerTree?.data?.layer?.type === LayerTypes.VIRTUAL_LAYER;
  return <>
    <div
      onClick={clickHandler}
      style={{ paddingLeft: `${props.offset * 28}px`, color: getColorFromString(layerTree.data?.layer.color) }}
      className={`folder-name pointer ${layerId === layerTree.data?.layer.id ? 'selected' : ''}`}
    >
      {layerTree.isLoading ? <span>Loading...</span> : (
        <>
          <span className="me-2">
            {layerTree.data?.layer?.isHidden ? <Icons.Hidden /> : <Icons.Visible />}
          </span>
          {isFolder && <span className="layer-icon-wrapper"><Icons.Folder /></span>}
          {isVirtualLayer && <span className="layer-icon-wrapper"><Icons.VirtualLayer /></span>}
          {!isVirtualLayer && !isFolder && <span className="layer-icon-wrapper"><Icons.Layer /></span>}
          {layerTree.data?.layer?.name}
        </>
      )}
    </div>
    {open && layerTree.data?.children?.map(item => {
      return <Tree key={item.childLayerId} layerId={item.childLayerId} offset={props.offset + 1} />;
    })}
  </>;
};

const Path = (props: { layerId: string }): JSX.Element => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const configId = params.configId as  string;
  const layerId = searchParams.get('selectedLayerId') || props.layerId;
  const layerTree = useQuery<LayerTree>({ path: `/map-layers/tree/${layerId}?configurationId=${configId}`, options: { keepPreviousData: true } });

  return <Page.Title>
    <span>{layerTree.data?.layer.name}</span>
    {(layerTree.data?.layer?.type === 'VIRTUAL_LAYER' || layerTree.data?.layer?.type === 'AGS_LAYER') && (
      <Link to={`/map-layers/${layerId}`} className="page-header-link">Open with map layer editor</Link>
    )}
  </Page.Title>;
};

export const TableOfContents = (): JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const layerId = params.layerId as string;

  useEffect(() => {
    if (searchParams.get('selectedLayerId') !== layerId) {
      navigate({ pathname: location.pathname, search: `selectedLayerId=${layerId}` }, { replace: true });
    }
  }, []);

  const tabItems = [
    { path: '', text: 'General' },
    { path: '/structure', text: 'Structure' },
    { path: '/parents', text: 'Parents' },
    { path: '/translations', text: 'Translations' },
  ];

  return <div>
    <Header>
      <Tenant />
      <Title>Table of Contents</Title>
      <SubTitle>Configure the table of contents structure for the DisasterAWARE system.</SubTitle>
    </Header>
    <div>
      <Sidebar className="sidebar-lg">
        <Tree layerId={layerId} offset={1} />
      </Sidebar>
      <ContentWrapper>
        <Content>
          <Page.Header className="d-flex justify-content-between">
            <div className="mb-2">
              <Path layerId={layerId} />
            </div>
          </Page.Header>
           <Tabs tabItems={tabItems} />
        </Content>
      </ContentWrapper>
    </div>
  </div>;
};

type Props = {
  tabItems: TabItem[];
};

type TabItem = {
  path: string;
  text: string;
};

const Tabs = (props: Props): JSX.Element => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const configId = params.configId as string;
  const layerId = params.layerId as string;
  const path = `/configurations/${configId}/contents/${layerId}`;
  const selectedLayerId = searchParams.get('selectedLayerId') || layerId;
  const layerTree = useQuery<LayerTree>({ path: `/map-layers/tree/${selectedLayerId}?configurationId=${configId}`, options: { keepPreviousData: true } });
  const isMutating = useIsMutating();

  return <>
    <div className="separator m-0" />
    <div className="tabs">
      {props.tabItems.map((tab: TabItem, index: number) => {
        const pathname = `${path}${tab.path}`;
        const match = useMatch(pathname);

        if (tab.text === 'Structure' && layerTree.data?.layer?.type !== LayerTypes.VIRTUAL_LAYER && layerTree.data?.layer?.type !== LayerTypes.FOLDER) {
          return null;
        }

        return <span key={tab.path} className="d-flex align-items-center">
          <Link to={{ pathname: pathname, search: location.search }} className={`tab-item ${match ? 'tab-item-selected' : ''}`}>{tab.text}</Link>
          {index !== props.tabItems.length - 1 ? <span className="tab-separator" /> : null}
        </span>;
      })}
    </div>
    <div className="separator m-0 mb-4" />
    <Routes>
      <Route path="/" element={<General />} />
      <Route path="/structure" element={<Structure />} />
      <Route path="/parents" element={<Parents />} />
      <Route path="/translations" element={<div>
        <Translations layerId={selectedLayerId} />
        <div className="d-flex justify-content-end">
          <LightBlack type="submit" form="translations-form" disabled={isMutating}>
            Translate
          </LightBlack>
          <Primary type="submit" className="ms-2" form="current-form" disabled={isMutating}>
            Save
          </Primary>
        </div>
      </div>} />
    </Routes>
  </>;
};
